import type { IssuedPairingCode, PeopleRoster, TeamMember } from "@don/wire";

import { expiresLabel, firstName } from "./team.js";

/**
 * Reactivate's rules, as arithmetic (epic 26-10-02-knowledge-v0, issue 218; ux-spec decisions
 * 14 and 19, design-spec §5 `reactivate-confirm`, `add-form-deactivated-match`, §6 with first
 * names, §11 C1 and D4). `reactivate.test.ts` asserts each.
 */

/**
 * Who a Reactivate is about: a deactivated row of the table, or the person Add's
 * `email_deactivated` named (`deactivated_at_ms` is `null` when no time was recorded).
 */
export type ReactivateTarget = Pick<TeamMember, "person_id" | "display_name" | "deactivated_at_ms">;

/** The confirm's words (design-spec §6 "Reactivate", first names for pronouns, §11 C1). */
export function reactivateCopy(target: Pick<TeamMember, "display_name">): {
  title: string;
  body: string;
  confirm: string;
  busy: string;
  cancel: string;
} {
  return {
    title: `Reactivate ${target.display_name}?`,
    body: `${firstName(target)} will need a new pairing code to set up the recorder again.`,
    confirm: "Reactivate",
    busy: "Reactivating…",
    cancel: "Cancel",
  };
}

/**
 * Add's offer band (design-spec §6 "Offer", ux-spec decision 14): "{Full name} was deactivated
 * on {Mon D}. Reactivate {First}?" and its button "Reactivate {First}". The date is the owner's
 * calendar day, no year, as `Expires` is; with no recorded time the sentence names no date.
 */
export function offerCopy(target: ReactivateTarget, timeZone?: string): { sentence: string; button: string } {
  const first = firstName(target);
  const when = target.deactivated_at_ms === null ? "" : ` on ${expiresLabel(target.deactivated_at_ms, timeZone)}`;
  return {
    sentence: `${target.display_name} was deactivated${when}. Reactivate ${first}?`,
    button: `Reactivate ${first}`,
  };
}

/** The page Notice when a reactivate fails (design-spec §6 "Page-level failures", §11 D4). */
export function reactivateFailedCopy(target: Pick<TeamMember, "display_name">): string {
  return `Couldn't reactivate ${firstName(target)}. Nothing was changed.`;
}

/**
 * The roster once the server has reactivated someone and answered their fresh code: the row is
 * current again, unpaired (no computer paired since now), with no live computer (every old one
 * stayed signed out) and the fresh code live, so `teamStatus` reads Pending invite with it
 * (decision 27) and `orderRoster` returns the row to the current group. What they shared is
 * untouched. Mirrors `registry::db::reactivate_person`.
 */
export function withReactivated(roster: PeopleRoster, issued: IssuedPairingCode): PeopleRoster {
  return {
    people: roster.people.map((m) =>
      m.person_id === issued.person_id
        ? {
            ...m,
            status: "active",
            deactivated_at_ms: null,
            paired: false,
            has_live_device: false,
            capture_reading: null,
            code_ever_issued: true,
            live_code: { code: issued.code, expires_at_ms: issued.expires_at_ms },
          }
        : m,
    ),
  };
}

/** The target a deactivated row names. */
export function targetOf(member: TeamMember): ReactivateTarget {
  return { person_id: member.person_id, display_name: member.display_name, deactivated_at_ms: member.deactivated_at_ms };
}
